import { TokenCurrency } from "@/api/supporter";
import { CurrencyCode, UpperCurrencyCode } from "@/constants/currency";

export type Bounds = [[SWLng: number, SWLat: number], [NELng: number, NELat: number]];

export type ChoroplethInfo = {
  name: string;
  getFeatures: () => Promise<GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>>;
  /**
   * Which key in the GeoJSON feature properties to use for the district name.
   */
  districtNameKey: string;
};

export type CountryInfo = {
  name: string;
  code: string;
  currency: UpperCurrencyCode;
  currencyLower: CurrencyCode;
  tokenCurrency: NonNullable<TokenCurrency>;
  bounds: Bounds;
  regions: Array<{ name: string; bounds: Bounds; code: string }>;
  choroplethMaps?: Array<ChoroplethInfo>;
};

async function fetchChoroplethMap(filename: string) {
  const res = await fetch(`/reporting-assets/${filename}.json`);
  if (!res.ok) {
    throw new Error(`Failed to load choropleth data for ${filename}`);
  }
  return (await res.json()) as GeoJSON.FeatureCollection<GeoJSON.Polygon | GeoJSON.MultiPolygon>;
}

/**
 * Data on individual countries to use for reporting.
 */
export const COUNTRY_DATA: Record<string, CountryInfo> = {
  AE: {
    name: "United Arab Emirates",
    code: "AE",
    currency: "AED",
    currencyLower: "aed",
    tokenCurrency: "aetoken",
    bounds: [
      [51.498, 22.633],
      [56.381, 26.084],
    ],
    regions: [],
  },
  AU: {
    name: "Australia",
    code: "AU",
    currency: "AUD",
    currencyLower: "aud",
    tokenCurrency: "autoken",
    bounds: [
      [112.921, -43.643],
      [153.638, -10.687],
    ],
    regions: [
      {
        name: "Australian Capital Territory",
        code: "ACT",
        bounds: [
          [148.761, -35.922],
          [149.402, -35.126],
        ],
      },
      {
        name: "New South Wales",
        code: "NSW",
        bounds: [
          [140.962, -39.135],
          [153.659, -28.153],
        ],
      },
      {
        name: "Northern Territory",
        code: "NT",
        bounds: [
          [128.997, -25.999],
          [137.995, -10.968],
        ],
      },
      {
        name: "Queensland",
        code: "QLD",
        bounds: [
          [137.974, -28.996],
          [153.633, -10.687],
        ],
      },
      {
        name: "South Australia",
        code: "SA",
        bounds: [
          [128.969, -38.061],
          [140.993, -25.988],
        ],
      },
      {
        name: "Tasmania",
        code: "TAS",
        bounds: [
          [143.822, -43.634],
          [148.479, -39.429],
        ],
      },
      {
        name: "Victoria",
        code: "VIC",
        bounds: [
          [140.955, -39.129],
          [149.971, -34.009],
        ],
      },
      {
        name: "Western Australia",
        code: "WA",
        bounds: [
          [112.951, -35.118],
          [129.055, -13.749],
        ],
      },
    ],
    choroplethMaps: [
      {
        name: "Cities",
        getFeatures: async () => {
          return await fetchChoroplethMap("au-tourism");
        },
        districtNameKey: "TR_NAME21",
      },
      {
        name: "States",
        getFeatures: async () => {
          return await fetchChoroplethMap("au-states");
        },
        districtNameKey: "STE_NAME21",
      },
    ],
  },
  CA: {
    name: "Canada",
    code: "CA",
    currency: "CAD",
    currencyLower: "cad",
    tokenCurrency: "catoken",
    bounds: [
      [-141, 41.681],
      [-52.619, 74.179],
    ],
    regions: [
      {
        code: "AB",
        name: "Alberta",
        bounds: [
          [-120, 49],
          [-110, 60],
        ],
      },
      {
        code: "BC",
        name: "British Columbia",
        bounds: [
          [-139, 48.3],
          [-114, 60],
        ],
      },
      {
        code: "MB",
        name: "Manitoba",
        bounds: [
          [-102, 49],
          [-88.9, 60],
        ],
      },
      {
        code: "NB",
        name: "New Brunswick",
        bounds: [
          [-69, 44.56],
          [-63.77, 48.07],
        ],
      },
      {
        code: "NL",
        name: "Newfoundland and Labrador",
        bounds: [
          [-67.82, 46.61],
          [-52.619, 60.37],
        ],
      },
      {
        code: "NT",
        name: "Northwest Territories",
        bounds: [
          [-136.46, 60],
          [-102, 70.63],
        ],
      },
      {
        code: "NS",
        name: "Nova Scotia",
        bounds: [
          [-66.4, 43.38],
          [-59.67, 47.22],
        ],
      },
      {
        code: "NU",
        name: "Nunavut",
        bounds: [
          [-120.71, 60],
          [-61.26, 74.179],
        ],
      },
      {
        code: "ON",
        name: "Ontario",
        bounds: [
          [-95.15, 41.681],
          [-74.34, 56.85],
        ],
      },
      {
        code: "PE",
        name: "Prince Edward Island",
        bounds: [
          [-64.41, 45.94],
          [-61.97, 47.05],
        ],
      },
      {
        code: "QC",
        name: "Quebec",
        bounds: [
          [-79.58, 45],
          [-57.1, 62.58],
        ],
      },
      {
        code: "SK",
        name: "Saskatchewan",
        bounds: [
          [-110, 49],
          [-101.3, 60],
        ],
      },
      {
        code: "YT",
        name: "Yukon",
        bounds: [
          [-141, 60],
          [-123.8, 69.64],
        ],
      },
    ],
  },
  IE: {
    name: "Ireland",
    code: "IE",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [-10.663, 51.419],
      [-5.997, 55.435],
    ],
    regions: [],
  },
  DE: {
    name: "Germany",
    code: "DE",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [5.866, 47.27],
      [15.042, 55.058],
    ],
    regions: [],
  },
  NL: {
    name: "Netherlands",
    code: "NL",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [3.358, 50.75],
      [7.227, 53.555],
    ],
    regions: [],
  },
  MT: {
    name: "Malta",
    code: "MT",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [14.18, 35.78],
      [14.58, 36.08],
    ],
    regions: [],
  },
  ES: {
    name: "Spain",
    code: "ES",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [-9.393, 35.946],
      [4.591, 43.748],
    ],
    regions: [],
  },
  EE: {
    name: "Estonia",
    code: "EE",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [21.764, 57.509],
      [28.21, 59.676],
    ],
    regions: [],
  },
  FR: {
    name: "France",
    code: "FR",
    currency: "EUR",
    currencyLower: "eur",
    tokenCurrency: "eutoken",
    bounds: [
      [-5.142, 41.333],
      [9.562, 51.089],
    ],
    regions: [],
  },
  NZ: {
    name: "New Zealand",
    code: "NZ",
    currency: "NZD",
    currencyLower: "nzd",
    tokenCurrency: "nztoken",
    bounds: [
      [166.425, -47.29],
      [178.55, -34.394],
    ],
    regions: [
      {
        name: "Northland",
        code: "NTL",
        bounds: [
          [172.635, -36.393],
          [174.783, -34.393],
        ],
      },
      {
        name: "Auckland",
        code: "AUK",
        bounds: [
          [174.16, -37.292],
          [175.291, -36.117],
        ],
      },
      {
        name: "Waikato",
        code: "WKO",
        bounds: [
          [174.657, -38.011],
          [175.544, -37.015],
        ],
      },
      {
        name: "Bay of Plenty",
        code: "BOP",
        bounds: [
          [175.805, -39.005],
          [178.107, -37.373],
        ],
      },
      {
        name: "Gisbourne",
        code: "GIS",
        bounds: [
          [177.116, -38.975],
          [178.549, -37.53],
        ],
      },
      {
        name: "Hawke's Bay",
        code: "HKB",
        bounds: [
          [176.042, -40.428],
          [178.001, -38.583],
        ],
      },
      {
        name: "Taranaki",
        code: "TKI",
        bounds: [
          [173.751, -39.871],
          [175.002, -38.71],
        ],
      },
      {
        name: "Manawatū-Whanganui",
        code: "MWT",
        bounds: [
          [174.675, -40.779],
          [176.632, -38.47],
        ],
      },
      {
        name: "Wellington",
        code: "WGN",
        bounds: [
          [174.613, -41.613],
          [176.332, -40.659],
        ],
      },
      {
        name: "Tasman",
        code: "TAS",
        bounds: [
          [172.05, -42.305],
          [173.304, -40.498],
        ],
      },
      {
        name: "Nelson",
        code: "NSN",
        bounds: [
          [173.197, -41.393],
          [173.599, -41.05],
        ],
      },
      {
        name: "Marlborough",
        code: "MBH",
        bounds: [
          [172.716, -42.484],
          [174.392, -40.663],
        ],
      },
      {
        name: "West Coast",
        code: "WTC",
        bounds: [
          [168.051, -44.5],
          [172.687, -40.777],
        ],
      },
      {
        name: "Canterbury",
        code: "CAN",
        bounds: [
          [169.542, -45.08],
          [174.063, -41.907],
        ],
      },
      {
        name: "Otago",
        code: "OTA",
        bounds: [
          [168.116, -46.639],
          [171.145, -43.955],
        ],
      },
      {
        name: "Southland",
        code: "STL",
        bounds: [
          [166.425, -47.289],
          [169.279, -44.255],
        ],
      },
      {
        name: "North Island",
        code: "NI",
        bounds: [
          [172.635, -41.613],
          [178.55, -34.394],
        ],
      },
      {
        name: "South Island",
        code: "SI",
        bounds: [
          [166.425, -47.289],
          [174.392, -40.498],
        ],
      },
    ],
    choroplethMaps: [
      {
        name: "Cities",
        getFeatures: async () => {
          return await fetchChoroplethMap("nz-urban-rural");
        },
        districtNameKey: "UR2025_V1_00_NAME",
      },
      {
        name: "Districts",
        getFeatures: async () => {
          return await fetchChoroplethMap("nz-territorial-authorities-2025");
        },
        districtNameKey: "TA2025_V1_00_NAME",
      },
      {
        name: "Regions",
        getFeatures: async () => {
          return await fetchChoroplethMap("nz-regional-council-2025");
        },
        districtNameKey: "REGC2025_V1_00_NAME",
      },
    ],
  },
  MX: {
    name: "Mexico",
    code: "MX",
    currency: "MXN",
    currencyLower: "mxn",
    tokenCurrency: "mxtoken",
    bounds: [
      [-118.4, 14.53],
      [-86.7, 32.72],
    ],
    regions: [],
  },
  HK: {
    name: "Hong Kong",
    code: "HK",
    currency: "HKD",
    currencyLower: "hkd",
    tokenCurrency: "hktoken",
    bounds: [
      [113.835, 22.153],
      [114.408, 22.561],
    ],
    regions: [],
  },
  CN: {
    name: "China",
    code: "CN",
    currency: "CNY",
    currencyLower: "cny",
    tokenCurrency: "cntoken",
    bounds: [
      [73.5, 18.2],
      [134.77, 53.56],
    ],
    regions: [],
  },
  IL: {
    name: "Israel",
    code: "IL",
    currency: "ILS",
    currencyLower: "ils",
    tokenCurrency: "iltoken",
    bounds: [
      [34.27, 29.5],
      [35.9, 33.34],
    ],
    regions: [],
  },
  SG: {
    name: "Singapore",
    code: "SG",
    currency: "SGD",
    currencyLower: "sgd",
    tokenCurrency: "sgtoken",
    bounds: [
      [103.605, 1.158],
      [104.088, 1.47],
    ],
    regions: [],
  },
  GB: {
    name: "United Kingdom",
    code: "GB",
    currency: "GBP",
    currencyLower: "gbp",
    tokenCurrency: "gbtoken",
    bounds: [
      [-8.177, 49.956],
      [1.763, 58.671],
    ],
    regions: [
      {
        name: "England",
        code: "ENG",
        bounds: [
          [-5.745, 49.949],
          [1.802, 55.824],
        ],
      },
      {
        name: "Scotland",
        code: "SCT",
        bounds: [
          [-7.624, 54.65],
          [-1.758, 58.671],
        ],
      },
      {
        name: "Wales",
        code: "WLS",
        bounds: [
          [-5.34, 51.384],
          [-2.66, 53.431],
        ],
      },
      {
        name: "Northern Ireland",
        code: "NIR",
        bounds: [
          [-8.177, 54.027],
          [-5.446, 55.381],
        ],
      },
    ],
  },
  US: {
    name: "United States",
    code: "US",
    currency: "USD",
    currencyLower: "usd",
    tokenCurrency: "ustoken",
    bounds: [
      [-124.736, 24.544],
      [-66.949, 49.382],
    ],
    regions: [],
  },
} as const;

/**
 * Special country-selector value meaning "combine every country".
 * Not a key of {@link COUNTRY_DATA} — check for it before looking a country up.
 */
export const ALL_COUNTRIES = "ALL";

/**
 * Every distinct token currency across the countries in {@link COUNTRY_DATA}.
 * Several EUR countries (IE, DE, NL, MT, ES, EE, FR) share `eutoken`, so this list is
 * shorter than the country list and safe to use without double-counting.
 */
export const ALL_TOKEN_CURRENCIES: Array<NonNullable<TokenCurrency>> = [
  ...new Set(Object.values(COUNTRY_DATA).map(c => c.tokenCurrency)),
];
